import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, cn, Icon, UserAvatar } from "@nexus/ui";
import { TenantMark } from "@/features/llm-usage/components/TenantMark";
import { useCopyToClipboard } from "@/hooks/useCopyToClipboard";
import { EVENT_DETAIL_ROW_GRID } from "./EventDetailSection";

const EMPTY_VALUE = "—";
const COPIED_RESET_MS = 2_000;

function CopyValueButton({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation();
  const { copied, copy } = useCopyToClipboard({ resetMs: COPIED_RESET_MS });

  return (
    <Button
      variant="ghost"
      size="icon-xs"
      className={cn(
        "shrink-0 rounded-sm transition-opacity",
        "min-h-11 min-w-11",
        "[@media(hover:hover)_and_(pointer:fine)]:size-5",
        "[@media(hover:hover)_and_(pointer:fine)]:min-h-0",
        "[@media(hover:hover)_and_(pointer:fine)]:min-w-0",
        !copied && "[@media(hover:hover)_and_(pointer:fine)]:opacity-0",
        "group-hover/field:opacity-100 focus-visible:opacity-100",
      )}
      onClick={() => void copy(value)}
      aria-label={
        copied
          ? t("admin.events.drawer.copied", "Copied")
          : t("admin.events.drawer.copyField", "Copy {{field}}", { field: label })
      }
    >
      <Icon
        name={copied ? "check" : "copy"}
        size="xs"
        colorVar={copied ? "--success" : undefined}
        aria-hidden
      />
    </Button>
  );
}

export function EventIdentityRow({
  label,
  value,
  display,
  mono = false,
  copyable = false,
}: {
  label: string;
  value: string;
  display?: ReactNode;
  mono?: boolean;
  copyable?: boolean;
}) {
  const isEmpty = value === EMPTY_VALUE;

  return (
    <div className={cn("group/field", EVENT_DETAIL_ROW_GRID, display ? "items-center" : null)}>
      <dt className="text-xs leading-5 text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "leading-5",
          !display && (mono ? "break-all font-mono text-[11px]" : "text-xs"),
          isEmpty && "text-muted-foreground",
        )}
      >
        {display ?? value}
      </dd>
      <dd>{copyable && !isEmpty ? <CopyValueButton value={value} label={label} /> : null}</dd>
    </div>
  );
}

export function EventActorIdentity({
  name,
  photoUrl,
}: {
  name: string | null;
  photoUrl?: string | null;
}) {
  if (!name) {
    return <span className="text-xs text-muted-foreground">{EMPTY_VALUE}</span>;
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <UserAvatar name={name} src={photoUrl} size="sm" className="shrink-0" />
      <span className="truncate text-xs">{name}</span>
    </span>
  );
}

export function EventTenantIdentity({
  tenantId,
  tenantName,
  tenantLogoUrl,
  tenantBrandColor,
}: {
  tenantId: string | null;
  tenantName: string | null;
  tenantLogoUrl?: string | null;
  tenantBrandColor?: string | null;
}) {
  if (!tenantId) {
    return <span className="text-xs text-muted-foreground">{EMPTY_VALUE}</span>;
  }

  return (
    <span className="flex min-w-0 items-center gap-2">
      <TenantMark name={tenantName} logoUrl={tenantLogoUrl} brandColor={tenantBrandColor} />
      <span className="truncate text-xs">{tenantName ?? tenantId}</span>
    </span>
  );
}

export { EMPTY_VALUE };
